import { createContext } from 'react'
import type { ActivityTrackId, SpinTier, UserGamificationProfile } from '../types/schema'
import type {
  ClaimTrackTicketsResult,
  EquippableSlotField,
  ProfileShowcasePatch,
  UnlockArrayField,
} from '../services/gamificationService'

export interface GamificationContextValue {
  /** Live `users/{uid}/gamification/profile` doc, hydrated; null until first load. */
  profile: UserGamificationProfile | null
  isLoading: boolean
  /** Live spendable balance from `users/{uid}/wallet/personal`. */
  ledgerCredits: number
  /** Active workspace currently afflicted by a Boss Curse (Phase 4). */
  isWorkspaceCursed: boolean
  /** True while the profile snapshot is served from offline cache rather than server-confirmed. */
  isProfileFromCache: boolean
  /** Optimistic theme selection awaiting server confirmation via callable or live snapshot. */
  pendingThemeId: string | null

  spin: (tier: SpinTier) => Promise<{ claimId: string; baseValue: number; tier: SpinTier }>
  claimSpin: (claimId: string, doubled: boolean) => Promise<void>
  /** Fire-and-forget passive event; the ExperienceEngine batches and processes it server-side. */
  recordTrack: (trackId: ActivityTrackId, increment?: number) => void
  /** Collect any spin tickets locked on `trackId`'s card into the live counters. */
  claimTrack: (trackId: ActivityTrackId) => Promise<ClaimTrackTicketsResult | null>
  rescueStreak: (viaAds: boolean) => Promise<void>

  equip: (patch: Partial<Record<EquippableSlotField, string>>) => Promise<void>
  /** Server-confirmed cosmetic unlock — rejects on App Check / rules / registry mismatch. */
  unlock: (field: UnlockArrayField, cosmeticId: string) => Promise<void>
  saveShowcase: (patch: ProfileShowcasePatch) => Promise<void>
}

export const GamificationContext = createContext<GamificationContextValue | undefined>(undefined)
